import { Box, Button, Stack } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DialogPromptMatchGame } from '../../api/DialogPromptApi';
import { UserDialogPromptApi } from '../../api/UserDialogPromptApi';
import { IVocabularyWord } from '../../api/VocabularyWordApi';
import AppBarContext from '../../context/AppBarContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import ChatBubble from '../ChatBubble';
import { handleErrorMessage } from '../ErrorDialog';
import MatchingGame, { MatchingGameMatch } from '../MatchingGame';
import { getAudioByText } from './DialogPromptComponent';


interface DialogPromptMatchGameComponentProps {
    dialogPrompt: DialogPromptMatchGame;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

const DialogPromptMatchGameComponent: React.FC<DialogPromptMatchGameComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef, isSettings = false }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const [chatBubbleDone, setChatBubbleDone] = React.useState(false);
    const [componentList, setComponentList] = React.useState<any[]>([]);
    const [chatBubbleIncorrectDone, setChatBubbleIncorrectDone] = React.useState<boolean[]>([]);
    const [chatBubbleCorrectDone, setChatBubbleCorrectDone] = React.useState<boolean>(false);

    const [matchList, setMatchList] = React.useState<MatchingGameMatch[]>([]);
    const [shuffledLeftList, setShuffledLeftList] = React.useState<IVocabularyWord[]>([]);
    const [shuffledRightList, setShuffledRightList] = React.useState<IVocabularyWord[]>([]);
    const [correctVocabularyWordIds, setCorrectVocabularyWordIds] = React.useState<number[]>([]);
    const [hasSubmitted, setHasSubmitted] = React.useState(false);
    const matchComplete = matchList.length == shuffledLeftList.length;

    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [yesResponseAudio, setYesResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [noResponseAudio, setNoResponseAudio] = React.useState<Blob | null | undefined>(undefined);

    const shuffle = <T,>(array: T[]): T[] => [...array].sort(() => Math.random() - 0.5);

    React.useEffect(() => {
        clearFooterList()
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.yes_response, setYesResponseAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.no_response, setNoResponseAudio, isSettings)
    }, [clearFooterList, dialogPrompt.no_response, dialogPrompt.prompt, dialogPrompt.yes_response, history, isSettings, setErrorMessage])

    React.useEffect(() => {
        setShuffledLeftList(shuffle(dialogPrompt.vocabulary_word_list));
        setShuffledRightList(shuffle(dialogPrompt.vocabulary_word_list));
    }, [chatBubbleDone, dialogPrompt.vocabulary_word_list]);

    const handleSubmit = React.useCallback(() => {
        setHasSubmitted(true);

        const completedGame = (
            <Box
                key={`matching-game-${componentList.length}`} // new key to avoid reuse
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <MatchingGame
                    instanceId={`matching-game-${componentList.length}`}
                    leftMatch={dialogPrompt.left_match}
                    rightMatch={dialogPrompt.right_match}
                    leftList={shuffledLeftList}
                    rightList={shuffledRightList}
                    matchList={matchList}
                    setMatchList={() => { }} // disable edits after submission
                    readOnly
                />
            </Box>
        );

        const newlyCorrectVocabularyWordIds = matchList
            .filter((match) => match.leftId == match.rightId)
            .map((match) => match.leftId);
        const nextCorrectVocabularyWordIds = Array.from(new Set([
            ...correctVocabularyWordIds,
            ...newlyCorrectVocabularyWordIds,
        ]));
        setCorrectVocabularyWordIds(nextCorrectVocabularyWordIds);
        const correct = nextCorrectVocabularyWordIds.length >= dialogPrompt.vocabulary_word_list.length;
        let chatBubble: React.ReactNode;
        if (correct) {
            chatBubble = (
                <ChatBubble
                    isTeacher={true}
                    message={dialogPrompt.yes_response}
                    messageAudio={yesResponseAudio ? yesResponseAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setChatBubbleCorrectDone}
                />
            )
            UserDialogPromptApi.addCorrectAnswer(dialogPrompt.id)
                .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })
        } else {
            chatBubble = (
                <ChatBubble
                    isTeacher={true}
                    message={dialogPrompt.no_response}
                    messageAudio={noResponseAudio ? noResponseAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={() =>
                        setChatBubbleIncorrectDone(previous => {
                            const updatedArray = [...previous];
                            updatedArray[componentList.length] = true;
                            return updatedArray;
                        })
                    }
                />
            )
            UserDialogPromptApi.addIncorrectAnswer(dialogPrompt.id)
                .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })

        }

        setComponentList((prev) => [...prev, completedGame, chatBubble]);
        setActionList([]);
    }, [
        componentList.length, dialogPrompt.left_match, dialogPrompt.right_match,
        dialogPrompt.vocabulary_word_list, dialogPrompt.yes_response, dialogPrompt.no_response,
        dialogPrompt.id, shuffledLeftList, shuffledRightList, matchList, correctVocabularyWordIds,
        yesResponseAudio, noResponseAudio, showAvatar, chatBubbleContainerRef,
        setChatBubbleCorrectDone, setChatBubbleIncorrectDone, setActionList, setErrorMessage, history, location,
    ]);

    React.useEffect(() => {
        if (hasSubmitted) {
            setActionList([]);
            return;
        }

        if (shuffledLeftList.length > 0 && shuffledRightList.length > 0) {
            setActionList([
                <Stack direction={{ xs: 'column', sm: 'row' }}
                    alignItems="center" // prevents vertical stretching in row layout
                >
                    <MatchingGame
                        instanceId={"actionList"}
                        leftMatch={dialogPrompt.left_match}
                        rightMatch={dialogPrompt.right_match}
                        leftList={shuffledLeftList}
                        rightList={shuffledRightList}
                        matchList={matchList}
                        setMatchList={setMatchList}
                    />
                    <Button key="submit-button"
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{
                            ml: { xs: 0, sm: 4 },          // no left margin on narrow screens
                            mt: { xs: 2, sm: 0 },          // add top margin in column mode
                            alignSelf: { xs: 'center', sm: 'auto' },  // center in column mode
                        }}
                        color="secondary"
                        disabled={!matchComplete}>
                        Submit
                    </Button>
                </Stack>
            ]);
        } else {
            setActionList([]);
        }
    }, [hasSubmitted, matchList, shuffledLeftList, shuffledRightList, matchComplete, dialogPrompt.left_match, dialogPrompt.right_match, handleSubmit, setActionList])

    React.useEffect(() => {
        if (chatBubbleIncorrectDone[componentList.length - 2]) {
            const remainingVocabularyWords = dialogPrompt.vocabulary_word_list.filter(
                (word) => !correctVocabularyWordIds.includes(word.id)
            );
            setShuffledLeftList(shuffle(remainingVocabularyWords));
            setShuffledRightList(shuffle(remainingVocabularyWords));
            setMatchList([]);
            setHasSubmitted(false);
        }
    }, [chatBubbleIncorrectDone, componentList.length, correctVocabularyWordIds, dialogPrompt.vocabulary_word_list])

    React.useEffect(() => {
        if (chatBubbleCorrectDone) {
            setDialogPromptComplete(dialogPrompt.id)
        }
    }, [chatBubbleCorrectDone, dialogPrompt.id, setDialogPromptComplete])

    return (
        <Stack spacing={2}>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                setChatBubbleDone={setChatBubbleDone}
                chatBubbleContainerRef={chatBubbleContainerRef}
            />
            {componentList.map((component, index) => (
                <Box key={index} component="div">
                    {component}
                </Box>
            ))}
        </Stack>
    );
};

export default DialogPromptMatchGameComponent;
